import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from 'korint-sdk/react';
import { DocumentReviewFilters as LegacyDocumentReviewFilters } from './legacy/DocumentReviewFilters';
import { Button } from '@folio/components/ui/button';
import { cn } from '@folio/lib';
import type { DocumentReviewFilter, DocumentReviewSummary } from './document-review';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { Badge } from '@folio/components/ui/badge';

type DocumentReviewFiltersProps = {
  summary: DocumentReviewSummary;
  filter: DocumentReviewFilter;
  onFilterChange: (filter: DocumentReviewFilter) => void;
  showAttention?: boolean;
  /** Policy document review: « À valider » first, segmented tabs, processing shown in the list. */
  reviewDesign?: boolean;
};

function DocumentReviewFiltersV2({
  summary,
  filter,
  onFilterChange,
  showAttention = true,
  reviewDesign = false,
}: DocumentReviewFiltersProps) {
  const { t } = useTranslation();
  const unclassifiedCount = summary.unclassifiedCount ?? 0;
  const counts = {
    all: summary.receivedCount,
    attention: summary.attentionCount,
    unclassified: unclassifiedCount,
    missing: summary.missingRequiredCount,
  };
  const unclassifiedOption = unclassifiedCount > 0 ? (['unclassified'] as const) : [];
  const options: DocumentReviewFilter[] = !showAttention
    ? ['all', 'missing']
    : reviewDesign
      ? ['attention', 'all', 'missing', ...unclassifiedOption]
      : ['attention', ...unclassifiedOption, 'all', 'missing'];

  return (
    <Stack gap={3}>
      <Group
        role="group"
        aria-label={t('documentReview.filterLabel')}
        aria-busy={summary.isLoading}
        wrap
        gap={reviewDesign ? 1 : 2}
        className={reviewDesign ? 'self-start rounded-lg bg-muted p-1' : 'border-b pb-4'}
      >
        {options.map(option => (
          <Button
            key={option}
            type="button"
            variant="ghost"
            size="sm"
            disabled={summary.isLoading}
            aria-pressed={filter === option}
            onClick={() => onFilterChange(option)}
            className={cn(
              'gap-2',
              reviewDesign
                ? cn(
                    'text-muted-foreground',
                    filter === option &&
                      'bg-background text-foreground shadow-sm hover:bg-background',
                  )
                : filter === option && 'bg-accent text-primary',
            )}
          >
            {t(
              reviewDesign && option === 'attention'
                ? 'documentReview.filter.toValidate'
                : `documentReview.filter.${option}`,
            )}
            <Badge
              variant="light"
              size="sm"
              className={cn(
                'tabular-nums',
                reviewDesign && filter === option && 'bg-slate-800 text-white',
              )}
              tone={!reviewDesign && option !== 'all' && counts[option] > 0 ? 'warning' : 'muted'}
            >
              {summary.isLoading ? '–' : counts[option]}
            </Badge>
          </Button>
        ))}
      </Group>
      <p className="sr-only" role="status">
        {summary.isLoading
          ? t('documentReview.loading')
          : [
              t('documentReview.received', { count: summary.receivedCount }),
              t('documentReview.missing', { count: summary.missingRequiredCount }),
              ...(showAttention
                ? [t('documentReview.attention', { count: summary.attentionCount })]
                : []),
            ].join('. ')}
      </p>
      {showAttention && !reviewDesign && summary.processingCount > 0 && (
        <Text size="sm" tone="muted" role="status">
          {t('documentReview.processing', { count: summary.processingCount })}
        </Text>
      )}
      {summary.hasError && (
        <Text size="sm" tone="destructive" role="alert">
          {t('documentReview.loadError')}
        </Text>
      )}
    </Stack>
  );
}

export function DocumentReviewFilters(props: DocumentReviewFiltersProps) {
  const documentReviewEnabled = useFeatureFlag('feat_document_review');
  return documentReviewEnabled ? (
    <DocumentReviewFiltersV2 {...props} />
  ) : (
    <LegacyDocumentReviewFilters
      {...props}
      filter={props.filter === 'unclassified' ? 'all' : props.filter}
    />
  );
}
